/**
 * member-channel-picker.tsx — Chọn nhiều "Kênh phụ trách" ngay trong hộp "Thêm nhân viên con người".
 *
 * Cùng nguồn dữ liệu với popup cột "Kênh phụ trách" (`employee-accounts-dialog.tsx`): `/zalo-accounts?connected=true`.
 * Kênh bị tắt hiện mờ kèm ghi chú, vẫn chọn tay được nhưng không nằm trong "Chọn tất cả".
 */
import { useMemo, useState } from 'react'
import { MessageSquare, Search, Wifi, WifiOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/misc'
import { cn } from '@/lib/utils'
import { CHANNELS, ChannelIcon } from '@/pages/integrations/channel-catalog'
import {
  allVisibleSelected,
  clearChannels,
  filterChannelOptions,
  selectAllChannels,
  toggleChannel,
  type ChannelOption,
} from './add-member-helpers'

function PlatformIcon({ platform }: { platform: number }) {
  const def = CHANNELS.find((c) => c.platforms?.includes(platform))
  if (def) return <ChannelIcon def={def} size="md" />
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
      <MessageSquare className="h-4 w-4" />
    </span>
  )
}

export function MemberChannelPicker({
  options,
  selected,
  onChange,
  loading,
  error,
}: {
  options: ChannelOption[]
  selected: string[]
  onChange: (ids: string[]) => void
  loading?: boolean
  error?: string
}) {
  const { t } = useTranslation('team')
  const [query, setQuery] = useState('')
  const visible = useMemo(() => filterChannelOptions(options, query), [options, query])
  const allOn = allVisibleSelected(selected, visible)

  if (loading) {
    return <p className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">{t('memberChannelPicker.loading')}</p>
  }
  if (options.length === 0) {
    return (
      <p className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
        {t('memberChannelPicker.noAccounts')}
      </p>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label={t('memberChannelPicker.searchAria')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('memberChannelPicker.searchPlaceholder')}
            className="h-9 pl-8"
          />
        </div>
        <button
          type="button"
          className="whitespace-nowrap text-xs font-medium text-primary underline-offset-2 hover:underline disabled:opacity-50"
          disabled={visible.length === 0}
          onClick={() => onChange(allOn ? clearChannels(selected, visible) : selectAllChannels(selected, visible))}
        >
          {allOn ? t('common:actions.deselectAll') : t('common:actions.selectAll')}
        </button>
      </div>

      <ul className={cn('max-h-56 divide-y overflow-y-auto rounded-lg border', error && 'border-destructive')}>
        {visible.length === 0 ? (
          <li className="px-3 py-3 text-center text-xs text-muted-foreground">{t('memberChannelPicker.noMatch')}</li>
        ) : (
          visible.map((o) => {
            const checked = selected.includes(o.id)
            return (
              <li key={o.id}>
                <label
                  className={cn(
                    'flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-accent/50',
                    o.disabled && 'opacity-60',
                  )}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => onChange(toggleChannel(selected, o.id))}
                    aria-label={o.name}
                  />
                  <PlatformIcon platform={o.platform} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium" title={o.name}>{o.name}</span>
                    <span className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                      <span>{o.platformLabel}</span>
                      <span aria-hidden>·</span>
                      <span className={cn('inline-flex items-center gap-1', o.online && 'text-success')}>
                        {o.online ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                        {o.online ? t('memberChannelPicker.online') : t('memberChannelPicker.offline')}
                      </span>
                      {o.disabled && <span className="text-amber-700 dark:text-amber-300">· {t('memberChannelPicker.disabled')}</span>}
                    </span>
                  </span>
                </label>
              </li>
            )
          })
        )}
      </ul>
      <p className={cn('text-xs', error ? 'text-destructive' : 'text-muted-foreground')}>
        {error ?? (selected.length > 0 ? t('memberChannelPicker.selectedCount', { count: selected.length }) : t('memberChannelPicker.notRequired'))}
      </p>
    </div>
  )
}
